import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { TokenContext } from '../providers/token/TokenProvider';

function ProtectedRoute({ children, requireAdmin = false }) {
  // Select individual values to ensure React detects changes
  const isLoaded = TokenContext.useSelector((state) => state.context.isLoaded);
  const isAuthenticated = TokenContext.useSelector((state) => state.context.isAuthenticated);
  const isAdmin = TokenContext.useSelector((state) => state.context.isAdmin);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [authFailureReason, setAuthFailureReason] = useState(null);

  // Debug logging to verify selector is working
  console.log('ProtectedRoute state:', { isLoaded, isAuthenticated, isAdmin, hasTimedOut });

  // Add timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isLoaded) {
        console.warn('Authentication check timed out after 5 seconds');
        setHasTimedOut(true);
        setAuthFailureReason('timeout');
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
  }, [isLoaded]);

  // Detect auth failure reason when loaded but not authenticated
  useEffect(() => {
    if (isLoaded && !isAuthenticated && !authFailureReason) {
      // Auth check completed but user is not authenticated
      setAuthFailureReason('not_authenticated');
    }
  }, [isLoaded, isAuthenticated, authFailureReason]);

  // Show loading while authentication state is being determined
  if (!isLoaded && !hasTimedOut) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  // Show login prompt on timeout or auth failure
  if (hasTimedOut || (isLoaded && !isAuthenticated)) {
    return <LoginPrompt reason={authFailureReason} />;
  }

  // Check admin requirement if specified
  if (requireAdmin && !isAdmin) {
    return <AccessDenied />;
  }

  return children;
}

function LoginPrompt({ reason }) {
  const { loginWithRedirect } = useAuth0();

  // Determine message based on failure reason
  let title = 'Please Log In';
  let message = 'You need to be logged in to access this page.';

  if (reason === 'timeout') {
    title = 'Authentication Timeout';
    message = 'The authentication check took too long. Please try logging in again.';
  } else if (reason === 'not_authenticated') {
    // Check if this might be an expired session
    const lastLogin = localStorage.getItem('last_login_time');
    if (lastLogin) {
      const hoursSinceLogin = (Date.now() - parseInt(lastLogin, 10)) / (1000 * 60 * 60);
      if (hoursSinceLogin > 12) {
        title = 'Session Expired';
        message = 'Your session has expired. Please log in again to continue.';
      }
    }
  }

  const handleLogin = () => {
    // Preserve intended destination for post-login redirect
    const currentPath = window.location.pathname + window.location.search + window.location.hash;

    // Store the intended destination
    sessionStorage.setItem('auth_redirect_path', currentPath);

    loginWithRedirect({
      appState: { returnTo: currentPath },
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: 2,
      }}
    >
      <Typography variant="h4" color="primary">
        {title}
      </Typography>
      <Typography variant="body1" sx={{ textAlign: 'center', mb: 2, maxWidth: '400px' }}>
        {message}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={handleLogin}
        sx={{ minWidth: '200px' }}
      >
        Log In
      </Button>
      <Button
        variant="outlined"
        onClick={() => window.location.href = '/'}
        sx={{ minWidth: '200px' }}
      >
        Return to Home
      </Button>
    </Box>
  );
}

LoginPrompt.propTypes = {
  reason: PropTypes.string,
};

function AccessDenied() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: 2,
      }}
    >
      <Typography variant="h4" color="error">
                Access Denied
      </Typography>
      <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                You don&apos;t have permission to access this area.
                Admin privileges are required.
      </Typography>
      <Button
        variant="contained"
        onClick={() => window.location.href = '/'}
      >
                Return to Home
      </Button>
    </Box>
  );
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requireAdmin: PropTypes.bool,
};

export default ProtectedRoute;
